/* `/nagrania`, variant A of `docs/design/nagrania-klientow-2026-09-24/`: the header, the client list
 * and the wall. The page and its measure are `shared/ui/canonScreen`; the tiles are
 * `entities/exercise-clip`'s. Both cards stand from the first frame at the body's full height, so
 * nothing on the screen changes size while it fills. */

/* ── Header ─────────────────────────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  flex: none;
  gap: var(--space-14px);
  align-items: center;
}

.headAvatar {
  --sz-avatar: var(--sz-recordings-head-avatar);

  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-recordings-head-avatar);
  height: var(--sz-recordings-head-avatar);
  background: var(--bg-chip);
  border-radius: var(--r-circle);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sub {
  margin: var(--space-0);
  font-size: var(--fs-label-md);
  line-height: var(--lh-name);
  color: var(--text-2);
}

.skelSub {
  display: inline-block;
  width: var(--w-recordings-sub-wait);
  height: var(--fs-label-md);
  vertical-align: middle;
}

.cardLink {
  gap: var(--space-2);
  height: var(--h-recordings-action);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  text-decoration: none;
  border-radius: var(--radius-sm);
}

.cardGlyph {
  display: block;
  width: var(--sz-recordings-glyph);
  height: var(--sz-recordings-glyph);
}

/* ── Body: the list beside the wall ─────────────────────────────────────────────────────────── */

.body {
  display: flex;
  flex: 1;
  gap: var(--space-20px);
  min-height: 0;
}

.clients,
.wall {
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* ── „Klienci" ──────────────────────────────────────────────────────────────────────────────── */

.clients {
  display: flex;
  flex: none;
  flex-direction: column;
  width: var(--w-recordings-clients);
  min-height: 0;
  padding: var(--space-4) var(--space-2) var(--space-2);
}

.clientsHead {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  padding: var(--space-0) var(--space-10px) var(--space-10px);
}

.clientsTitle {
  flex: 1;
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
}

.clientsOrder {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
}

.clientsNotice {
  margin: var(--space-0);
  padding: var(--space-2) var(--space-10px);
  font-size: var(--fs-label-md);
  color: var(--text-2);
}

.clientRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-height: 0;
  margin: var(--space-0);
  padding: var(--space-0);
  overflow: auto;
  list-style: none;
}

/* „Wszyscy klienci" stands over a hairline that separates it from the people. */
.allRow {
  padding-bottom: var(--space-6px);
  margin-bottom: var(--space-6px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.clientRow {
  --sz-avatar: var(--sz-recordings-avatar);

  position: relative;
}

.row {
  display: grid;
  grid-template-columns: var(--sz-recordings-avatar) minmax(0, 1fr) auto;
  gap: var(--space-2px) var(--space-3);
  align-items: center;
  width: 100%;
  padding: var(--space-9px) var(--space-10px);
  font: inherit;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.row:hover {
  background: var(--bg-inset);
}

.row[aria-current='true'] {
  background: var(--bg-chip);
}

.row:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.rowAvatar,
.allMark {
  grid-row: span 2;
}

.allMark {
  display: grid;
  place-items: center;
  width: var(--sz-recordings-avatar);
  height: var(--sz-recordings-avatar);
  color: var(--text-2);
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--r-circle);
}

.allGlyph {
  display: block;
  width: var(--sz-recordings-all-glyph);
  height: var(--sz-recordings-all-glyph);
}

.rowName {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  min-width: 0;
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-name);
}

.rowNameText {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The client has a recording the trainer has not watched: the one mark on the list in the accent. */
.unreadDot {
  flex: none;
  width: var(--sz-recordings-dot);
  height: var(--sz-recordings-dot);
  background: var(--accent);
  border-radius: var(--r-circle);
}

.rowMeta {
  grid-column: 2;
  overflow: hidden;
  font-size: var(--fs-label);
  line-height: var(--lh-name);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowCount {
  grid-row: 1 / span 2;
  grid-column: 3;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* The way to the client's card, over the count, while the row is pointed at or reached by Tab. */
.rowGo {
  position: absolute;
  top: 50%;
  right: var(--space-10px);
  display: grid;
  place-items: center;
  width: var(--sz-recordings-go);
  height: var(--sz-recordings-go);
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
  opacity: 0;
  transform: translateY(-50%);
}

.clientRow:hover .rowGo {
  opacity: 1;
}

.clientRow .rowGo:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
  opacity: 1;
}

.clientRow:hover .rowCount {
  visibility: hidden;
}

.goGlyph {
  display: block;
  width: var(--sz-recordings-glyph);
  height: var(--sz-recordings-glyph);
}

.skelRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-9px) var(--space-10px);
}

.skelAvatar {
  flex: none;
  width: var(--sz-recordings-avatar);
  height: var(--sz-recordings-avatar);
}

.skelLines {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6px);
}

.skelName {
  width: 60%;
  height: var(--fs-body);
}

.skelMeta {
  width: 80%;
  height: var(--fs-label);
}

/* ── The wall ───────────────────────────────────────────────────────────────────────────────── */

.wall {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
  padding: var(--space-14px) var(--space-20px) var(--space-22px);
  overflow: auto;
}

.wall:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.wallBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.empty {
  display: grid;
  flex: 1;
  place-items: center;
}

.notice {
  margin: var(--space-0);
  font-size: var(--fs-label-md);
  color: var(--text-2);
}
